import _ from 'lodash';

import { EMPTY_GUID } from 'constants/data';
import { DataType } from 'constants/taskCategories';
import { TaskAssignmentType, TaskStatus } from 'constants/tasks';
import {
  TASK_ACTION_COMPLETION_NOT_ALLOWED_MESSAGE,
  TASK_ACTION_LOAN_DETAILS_FAILED_MESSAGE,
  TASK_ACTION_NOT_AUTO_COMPLETABLE_MESSAGE,
  TASK_ACTION_UNAVAILABLE_FOR_LOAN_MESSAGE,
  TASK_ACTION_UNAVAILABLE_FOR_STATUS_MESSAGE,
  TASK_COMPLETION_SKIPPED_RESULT_TYPE,
  TaskActionCompletionStatus,
  TaskActionOriginType,
} from 'constants/taskTypeActions';

const CLOSED_TASK_STATUSES = new Set([TaskStatus.APPROVED, TaskStatus.CANCELED]);

export const getTaskActionExecuteRequest = ({
  taskId,
  applicationId,
  taskTypeAction,
  metadata = null,
}) => ({
  applicationId,
  origin: { type: TaskActionOriginType.TASK, id: taskId },
  loanActionTypes: taskTypeAction?.loanActionTypes ?? [],
  metadata,
});

/**
 * Whether a task's action button shows, and whether (and why) it is disabled. The reasons mirror
 * what the server would refuse up front: a Rejected task can't be completed by the action, a
 * ScheduleClosing task can't be completed automatically, a Submit or Approve completion needs the
 * approve/reject/cancel task claim; plus a run-flow-disabled action (e.g. Reset AUS findings with
 * no AUS results) and a runner context that failed to load or is still loading. A run that already
 * completed the task hides the button even while the host still shows the pre-run status.
 * @param {{
 *   status: string,
 *   onSuccessTaskStatus: string|null,
 *   taskDataType: string|null,
 *   isApproveAllowed: boolean,
 *   isUnavailableForLoan: boolean,
 *   isReady: boolean,
 *   isLoadFailed: boolean,
 *   isCompleted: boolean,
 * }} params
 * @returns {{ isVisible: boolean, isDisabled: boolean, disabledReason: string|null }}
 */
export const getTaskActionAvailability = ({
  status,
  onSuccessTaskStatus,
  taskDataType = null,
  isApproveAllowed,
  isUnavailableForLoan = false,
  isReady = true,
  isLoadFailed = false,
  isCompleted = false,
}) => {
  const isCompletionConfigured =
    !!onSuccessTaskStatus && onSuccessTaskStatus !== TaskActionCompletionStatus.NONE;

  const disabledReason =
    (isLoadFailed && TASK_ACTION_LOAN_DETAILS_FAILED_MESSAGE) ||
    (status === TaskStatus.REJECTED &&
      isCompletionConfigured &&
      TASK_ACTION_UNAVAILABLE_FOR_STATUS_MESSAGE) ||
    (taskDataType === DataType.SCHEDULE_CLOSING &&
      isCompletionConfigured &&
      TASK_ACTION_NOT_AUTO_COMPLETABLE_MESSAGE) ||
    (isCompletionConfigured && !isApproveAllowed && TASK_ACTION_COMPLETION_NOT_ALLOWED_MESSAGE) ||
    (isUnavailableForLoan && TASK_ACTION_UNAVAILABLE_FOR_LOAN_MESSAGE) ||
    null;

  return {
    isVisible: !CLOSED_TASK_STATUSES.has(status) && !(isCompleted && isCompletionConfigured),
    isDisabled: !!disabledReason || !isReady,
    disabledReason,
  };
};

/**
 * Task actions exist only for internal (SLA group) task types on loan tasks.
 * @param {{ taskAssignmentType: string, applicationId: string|null }} params
 * @returns {boolean}
 */
export const checkIsTaskTypeActionLocked = ({ items, isItemsEditAllowed }) =>
  !isItemsEditAllowed && !_.isEmpty(items);

export const checkIsTaskActionSupported = ({ taskAssignmentType, applicationId }) =>
  taskAssignmentType === TaskAssignmentType.SLA_GROUP &&
  !!applicationId &&
  applicationId !== EMPTY_GUID;

/**
 * Wraps a task type action as the single-action source the loan-action run flow expects.
 * @param {object} taskTypeAction TaskTypeActionDto
 * @returns {Array<object>}
 */
export const buildTaskActionSource = taskTypeAction => [
  {
    id: taskTypeAction.taskTypeId,
    name: taskTypeAction.buttonLabel,
    loanActionTypes: taskTypeAction.loanActionTypes ?? [],
    icon: taskTypeAction.icon ?? null,
    color: taskTypeAction.color ?? null,
    variant: taskTypeAction.variant ?? null,
  },
];

/**
 * The standalone execute endpoint adds a TaskCompletionSkipped result when the configured completion
 * was held (a Lock Loan / Save Pending Changes item soft-failed, an item failed, the task is already
 * closed) or the task could not be moved.
 * @param {Array|null} results LoanActionResult[]
 * @returns {object|null}
 */
export const getTaskCompletionSkippedResult = results =>
  (results ?? []).find(({ type }) => type === TASK_COMPLETION_SKIPPED_RESULT_TYPE) ?? null;

/**
 * Whether any item of a task action's type is one of `types`.
 * @param {object|null} taskTypeAction
 * @param {Array<string>} types
 * @returns {boolean}
 */
export const checkHasTaskActionItemType = (taskTypeAction, types) =>
  (taskTypeAction?.loanActionTypes ?? []).some(({ type }) => types.includes(type));
